// audioCallLogger — CU-869dafa5q.
//
// Wires per-call state-change loggers around the RTCPeerConnection, the
// remote audio MediaStreamTrack, and the HTMLAudioElement so a future
// investigator can read the timeline of a chopped call without guessing.
//
// Lifecycle: start() in setupAudioStream after play() succeeds; stop() in
// cleanupAudio, keyed by callId so a stale teardown can't disable a newer
// call's loggers (same ownership-keying pattern as audioDiagTick).

import logger from '../services/logger';
import {bumpDiagCounter, setAudioCallActive, isAudioDiagEnabled} from './audioDiagCounters';
import {resetAudioPageLoggerCallScopedState, armAudioPageLogger} from './audioPageLogger';

interface CallLoggerParams {
    callId: string;
    pc: RTCPeerConnection;
    remoteAudioTrack: MediaStreamTrack | null;
    audioElement: HTMLAudioElement;
}

type CallLoggerStopReason = 'call_end' | 'restart';
interface ActiveLogger {
    callId: string;
    stop: (reason: CallLoggerStopReason) => void;
}

let active: ActiveLogger | null = null;

// Codex review — persisted-across-restart state must be keyed by BOTH
// callId AND RTCPeerConnection reference. Pair IDs and inbound-RTP
// counters belong to a specific PC; on an ICE restart that swaps in a
// fresh PC, comparing the new PC's IDs/counters against the old PC's
// values is materially misleading. Keep state across same-call restarts
// only when the PC is the same object (e.g. logger re-attach without a
// renegotiation); reset on any PC swap.
interface PersistedCallState {
    callId: string;
    pc: RTCPeerConnection;
    prevPairId: string | null;
    prevReceiverStats: {
        packetsReceived: number; bytesReceived: number; packetsLost: number;
        emittedCount: number; jitterBufferDelaySec: number;
    } | null;
}
let persistedState: PersistedCallState | null = null;

function getPersistedState(callId: string, pc: RTCPeerConnection): PersistedCallState {
    if (persistedState && persistedState.callId === callId && persistedState.pc === pc) {
        return persistedState;
    }
    persistedState = {callId, pc, prevPairId: null, prevReceiverStats: null};
    return persistedState;
}

// Per-element listener events. Codex spec lists 15 — we use those that
// matter for "playout stalled vs. attached but silent" diagnosis. `error`
// is included because some browsers fire it instead of `stalled` when the
// MediaStream backing srcObject is torn down.
const AUDIO_ELEMENT_EVENTS: ReadonlyArray<keyof HTMLMediaElementEventMap> = [
    'play', 'playing', 'pause', 'waiting', 'stalled', 'suspend',
    'seeking', 'seeked', 'emptied', 'ended', 'error', 'ratechange',
    'volumechange', 'loadedmetadata', 'canplay',
];

function snapshotElementCommon(el: HTMLAudioElement): Record<string, unknown> {
    return {
        paused: el.paused,
        muted: el.muted,
        volume: el.volume,
        playbackRate: el.playbackRate,
        currentTime: el.currentTime,
        readyState: el.readyState,
        networkState: el.networkState,
        srcObjectPresent: el.srcObject != null,
        sinkId: typeof el.sinkId === 'string' ? el.sinkId : null,
    };
}

export function startAudioCallLogger(params: CallLoggerParams): () => void {
    // Retry-arm the page-level logger every call. If the page booted
    // with diag=off and the operator has since flipped it on, the
    // first arm-attempt was a no-op; this lets the next call install
    // the listeners. Idempotent — no-op after the first successful arm.
    armAudioPageLogger();
    // Kill switch — bail before installing any listeners or pollers.
    // CORE call setup (play, attach, output device, jitterBufferTarget,
    // senderPriority, contentHint) is in sipService and runs regardless.
    if (!isAudioDiagEnabled()) return () => { /* no-op */ };
    let isFreshCall = true;
    if (active) {
        // Codex review — callId === 'unknown' is a sipService fallback
        // when session.id is missing. Two unrelated calls can both land
        // here with callId='unknown'; treat them as different calls so
        // the per-call reset path runs.
        const sameCall = active.callId === params.callId && params.callId !== 'unknown';
        if (!sameCall) {
            logger.warn('Audio call logger: replacing logger for different callId', {
                source: 'sip', event: 'audio_call_logger_replaced',
                previousCallId: active.callId, newCallId: params.callId,
            });
            active.stop('call_end');
        } else {
            // Same-callId restart (re-INVITE / ICE restart) — don't drop
            // the active-call gate and don't wipe persistedState. The new
            // logger is installed synchronously below; any gap in
            // `setAudioCallActive` would suppress gated page-level
            // signals during exactly the restart window we want to
            // diagnose if a restart causes the chop.
            isFreshCall = false;
            active.stop('restart');
        }
    }

    const cleanups: Array<() => void> = [];
    // Codex review — async getStats() pollers must check this flag after
    // their awaits. Without it, a getStats() that resolves AFTER stop()
    // can still emit events and (worse) mutate persistedState in a way
    // that corrupts the next logger instance for the same callId.
    let stopped = false;

    // PC state transitions — connection / ice / iceGathering / signaling.
    // Each fires its own change event; we log a unified audio_pc_state_change
    // payload so a single Kibana query surfaces transport problems.
    {
        const pc = params.pc;
        const prev: Record<string, string> = {
            connection: pc.connectionState,
            iceConnection: pc.iceConnectionState,
            iceGathering: pc.iceGatheringState,
            signaling: pc.signalingState,
        };
        const makeListener = (changeType: 'connection' | 'iceConnection' | 'iceGathering' | 'signaling') => () => {
            const stateNow: Record<typeof changeType, string> = {
                connection: pc.connectionState,
                iceConnection: pc.iceConnectionState,
                iceGathering: pc.iceGatheringState,
                signaling: pc.signalingState,
            };
            logger.info('PC state change', {
                source: 'sip', event: 'audio_pc_state_change',
                callId: params.callId,
                changeType,
                prevState: prev[changeType],
                state: stateNow[changeType],
                connectionState: pc.connectionState,
                iceConnectionState: pc.iceConnectionState,
                iceGatheringState: pc.iceGatheringState,
                signalingState: pc.signalingState,
                visibilityState: document.visibilityState,
            });
            prev[changeType] = stateNow[changeType];
        };
        const onConn = () => {
            makeListener('connection')();
            if (pc.connectionState === 'failed') bumpDiagCounter('pcConnectionFailed');
            if (pc.connectionState === 'disconnected') bumpDiagCounter('pcDisconnected');
        };
        const onIce = makeListener('iceConnection');
        const onIceGather = makeListener('iceGathering');
        const onSig = makeListener('signaling');
        pc.addEventListener('connectionstatechange', onConn);
        pc.addEventListener('iceconnectionstatechange', onIce);
        pc.addEventListener('icegatheringstatechange', onIceGather);
        pc.addEventListener('signalingstatechange', onSig);
        cleanups.push(() => {
            pc.removeEventListener('connectionstatechange', onConn);
            pc.removeEventListener('iceconnectionstatechange', onIce);
            pc.removeEventListener('icegatheringstatechange', onIceGather);
            pc.removeEventListener('signalingstatechange', onSig);
        });
    }

    // Remote audio track lifecycle — mute/unmute is one of the cleanest
    // separations for "RTP stopped" vs "the network kept flowing but the
    // remote stream went silent for a different reason".
    if (params.remoteAudioTrack) {
        const track = params.remoteAudioTrack;
        const trackSnapshot = () => ({
            trackId: track.id,
            kind: track.kind,
            muted: track.muted,
            enabled: track.enabled,
            readyState: track.readyState,
        });
        const onMute = () => {
            bumpDiagCounter('trackMute');
            logger.warn('Remote track muted', {
                source: 'sip', event: 'audio_track_state_change',
                callId: params.callId, trackEvent: 'mute', ...trackSnapshot(),
                visibilityState: document.visibilityState,
            });
        };
        const onUnmute = () => logger.info('Remote track unmuted', {
            source: 'sip', event: 'audio_track_state_change',
            callId: params.callId, trackEvent: 'unmute', ...trackSnapshot(),
            visibilityState: document.visibilityState,
        });
        const onEnded = () => {
            bumpDiagCounter('trackEnded');
            logger.warn('Remote track ended', {
                source: 'sip', event: 'audio_track_state_change',
                callId: params.callId, trackEvent: 'ended', ...trackSnapshot(),
                visibilityState: document.visibilityState,
            });
        };
        track.addEventListener('mute', onMute);
        track.addEventListener('unmute', onUnmute);
        track.addEventListener('ended', onEnded);
        cleanups.push(() => {
            track.removeEventListener('mute', onMute);
            track.removeEventListener('unmute', onUnmute);
            track.removeEventListener('ended', onEnded);
        });
    }

    // HTMLAudioElement lifecycle. `waiting` and `stalled` are the two
    // events most diagnostic of "playout buffer ran dry" — Chrome fires
    // them when the element can't get enough decoded data to keep playing.
    {
        const el = params.audioElement;
        const listeners: Array<[keyof HTMLMediaElementEventMap, EventListener]> = [];
        AUDIO_ELEMENT_EVENTS.forEach((ev) => {
            const handler: EventListener = (e: Event) => {
                const errEvent = ev === 'error' ? (el.error ?? null) : null;
                if (ev === 'stalled') bumpDiagCounter('audioElementStalled');
                if (ev === 'waiting') bumpDiagCounter('audioElementWaiting');
                logger.info('Audio element state change', {
                    source: 'sip', event: 'audio_element_state_change',
                    callId: params.callId,
                    elementEvent: ev,
                    ...snapshotElementCommon(el),
                    errorCode: errEvent?.code ?? null,
                    errorMessage: errEvent?.message ?? null,
                    visibilityState: document.visibilityState,
                });
                void e;
            };
            el.addEventListener(ev, handler);
            listeners.push([ev, handler]);
        });
        cleanups.push(() => {
            listeners.forEach(([ev, handler]) => el.removeEventListener(ev, handler));
        });
    }

    // audio_selected_candidate_pair_change — codex Tier 3 follow-up.
    // Polls `getStats()` every 10 s, finds the currently-selected ICE
    // candidate pair, and logs only on transition. Important when an
    // operator's chop coincides with a network route change (Wi-Fi → LTE,
    // VPN reconnect, NAT rebinding): selected-pair change is the cleanest
    // single signal for that.
    {
        const callState = getPersistedState(params.callId, params.pc);
        const poll = async () => {
            if (stopped) return;
            try {
                const stats = await params.pc.getStats();
                if (stopped) return;
                let activePair: Record<string, unknown> | null = null;
                let localCandidate: Record<string, unknown> | null = null;
                let remoteCandidate: Record<string, unknown> | null = null;
                // Codex review — consult transport.selectedCandidatePairId
                // first as the authoritative answer. Scanning candidate-
                // pair `nominated` flags can return the wrong pair when
                // multiple nominated pairs exist (non-rtcp-mux, post-ICE-
                // checks).
                stats.forEach((r: unknown) => {
                    const report = r as Record<string, unknown>;
                    if (report.type === 'transport' && typeof report.selectedCandidatePairId === 'string') {
                        const target = stats.get(report.selectedCandidatePairId as string) as unknown as Record<string, unknown> | undefined;
                        if (target) activePair = target;
                    }
                });
                if (!activePair) {
                    // Fallback — no transport stat published a
                    // selectedCandidatePairId. Scan for any nominated /
                    // selected pair in a usable state. Prefer succeeded
                    // over in-progress to avoid latching on a half-
                    // negotiated path.
                    let succeededPair: Record<string, unknown> | null = null;
                    let inProgressPair: Record<string, unknown> | null = null;
                    stats.forEach((r: unknown) => {
                        const report = r as Record<string, unknown>;
                        if (report.type !== 'candidate-pair') return;
                        if (report.selected !== true && report.nominated !== true) return;
                        if (report.state === 'succeeded') succeededPair = succeededPair ?? report;
                        else if (report.state === 'in-progress') inProgressPair = inProgressPair ?? report;
                    });
                    activePair = succeededPair ?? inProgressPair;
                }
                if (!activePair) return;
                const pair = activePair as Record<string, unknown>;
                const pairId = typeof pair.id === 'string' ? pair.id : null;
                if (pairId == null || pairId === callState.prevPairId) return;
                if (typeof pair.localCandidateId === 'string') {
                    localCandidate = (stats.get(pair.localCandidateId) ?? null) as Record<string, unknown> | null;
                }
                if (typeof pair.remoteCandidateId === 'string') {
                    remoteCandidate = (stats.get(pair.remoteCandidateId) ?? null) as Record<string, unknown> | null;
                }
                const prevPairIdSnapshot = callState.prevPairId;
                const isFirstObservation = callState.prevPairId == null;
                callState.prevPairId = pairId;
                // Codex review — only bump the counter on a real
                // transition. The first observation is a baseline (we
                // emit it as phase='initial' for investigators who want
                // to know which pair the call started on); subsequent
                // changes are phase='change' and counted.
                if (!isFirstObservation) bumpDiagCounter('selectedCandidatePairChange');
                logger.warn('Selected candidate pair observed', {
                    source: 'sip', event: 'audio_selected_candidate_pair_change',
                    callId: params.callId,
                    phase: isFirstObservation ? 'initial' : 'change',
                    prevPairId: prevPairIdSnapshot, pairId,
                    localCandidateType: localCandidate?.candidateType ?? null,
                    remoteCandidateType: remoteCandidate?.candidateType ?? null,
                    networkType: localCandidate?.networkType ?? null,
                    protocol: localCandidate?.protocol ?? null,
                    relayProtocol: localCandidate?.relayProtocol ?? null,
                    availableIncomingBitrate: pair.availableIncomingBitrate ?? null,
                    currentRoundTripTimeMs: typeof pair.currentRoundTripTime === 'number'
                        ? (pair.currentRoundTripTime as number) * 1000 : null,
                    bytesSent: pair.bytesSent ?? null,
                    bytesReceived: pair.bytesReceived ?? null,
                    visibilityState: document.visibilityState,
                });
            } catch { /* getStats can fail on closed pc — swallow */ }
        };
        void poll();
        const intervalId = setInterval(() => { void poll(); }, 10000);
        cleanups.push(() => clearInterval(intervalId));
    }

    // audio_receiver_stats_snapshot — codex Tier 3 follow-up. The
    // 1 s/2 s diag_tick carries the "is anything moving" subset; this
    // 5 s narrow snapshot adds the decoder-side fields (concealment,
    // FEC, deceleration/acceleration, decoderImplementation) that
    // explain HOW the inbound RTP was decoded rather than just whether
    // it kept arriving.
    {
        const callState = getPersistedState(params.callId, params.pc);
        const poll = async () => {
            if (stopped) return;
            try {
                const stats = await params.pc.getStats();
                if (stopped) return;
                let r: Record<string, unknown> | null = null;
                stats.forEach((report: unknown) => {
                    const rep = report as Record<string, unknown>;
                    if (rep.type === 'inbound-rtp' && rep.kind === 'audio') r = rep;
                });
                if (!r) return;
                const rec = r as Record<string, unknown>;
                const packetsReceived = typeof rec.packetsReceived === 'number' ? (rec.packetsReceived as number) : 0;
                const bytesReceived = typeof rec.bytesReceived === 'number' ? (rec.bytesReceived as number) : 0;
                const packetsLost = typeof rec.packetsLost === 'number' ? (rec.packetsLost as number) : 0;
                const emittedCount = typeof rec.jitterBufferEmittedCount === 'number' ? (rec.jitterBufferEmittedCount as number) : 0;
                const jitterBufferDelay = typeof rec.jitterBufferDelay === 'number' ? (rec.jitterBufferDelay as number) : 0;
                // Codex review — preserving prev across same-callId
                // restart causes negative deltas because inbound RTP
                // counters reset to 0 on a fresh RTCPeerConnection. If
                // ANY tracked counter regressed, treat it as a baseline
                // reset: emit null deltas, persist the new baseline,
                // tag the event so investigators don't read it as a stat
                // drop. Logged-as-true `pcBaselineReset` is also a
                // useful "we saw the restart land" event for the
                // anomaly summary.
                const prev = callState.prevReceiverStats;
                const baselineReset = prev != null && (
                    packetsReceived < prev.packetsReceived
                    || bytesReceived < prev.bytesReceived
                    || packetsLost < prev.packetsLost
                );
                // Codex review — jitterBufferDelay and jitterBufferEmittedCount
                // are CUMULATIVE counters. A lifetime average understates
                // late-call buffering spikes (which is exactly what an
                // investigator chasing a chop wants to see). Compute the
                // 5 s window average from the delta. First sample and
                // post-reset samples have no prev → null.
                const windowEmitted = prev != null && !baselineReset ? emittedCount - prev.emittedCount : 0;
                const windowDelaySec = prev != null && !baselineReset ? jitterBufferDelay - prev.jitterBufferDelaySec : 0;
                const windowJitterBufferDelayMs = windowEmitted > 0
                    ? (windowDelaySec / windowEmitted) * 1000
                    : null;
                logger.info('Audio receiver stats snapshot', {
                    source: 'sip', event: 'audio_receiver_stats_snapshot',
                    callId: params.callId,
                    windowMs: 5000,
                    pcBaselineReset: baselineReset,
                    packetsReceived,
                    packetsReceivedDelta: prev != null && !baselineReset ? packetsReceived - prev.packetsReceived : null,
                    bytesReceived,
                    bytesReceivedDelta: prev != null && !baselineReset ? bytesReceived - prev.bytesReceived : null,
                    packetsLost,
                    packetsLostDelta: prev != null && !baselineReset ? packetsLost - prev.packetsLost : null,
                    jitter: rec.jitter ?? null,
                    jitterBufferDelayMs: windowJitterBufferDelayMs,
                    jitterBufferDelayLifetimeMs: emittedCount > 0 ? (jitterBufferDelay / emittedCount) * 1000 : null,
                    jitterBufferEmittedCount: emittedCount,
                    jitterBufferEmittedDelta: prev != null && !baselineReset ? windowEmitted : null,
                    audioLevel: rec.audioLevel ?? null,
                    totalAudioEnergy: rec.totalAudioEnergy ?? null,
                    totalSamplesDuration: rec.totalSamplesDuration ?? null,
                    concealedSamples: rec.concealedSamples ?? null,
                    concealmentEvents: rec.concealmentEvents ?? null,
                    insertedSamplesForDeceleration: rec.insertedSamplesForDeceleration ?? null,
                    removedSamplesForAcceleration: rec.removedSamplesForAcceleration ?? null,
                    silentConcealedSamples: rec.silentConcealedSamples ?? null,
                    fecPacketsReceived: rec.fecPacketsReceived ?? null,
                    decoderImplementation: rec.decoderImplementation ?? null,
                    ssrc: rec.ssrc ?? null,
                    trackIdentifier: rec.trackIdentifier ?? null,
                    playoutId: rec.playoutId ?? null,
                    visibilityState: document.visibilityState,
                });
                callState.prevReceiverStats = {
                    packetsReceived, bytesReceived, packetsLost,
                    emittedCount, jitterBufferDelaySec: jitterBufferDelay,
                };
            } catch { /* getStats can fail on closed pc — swallow */ }
        };
        const intervalId = setInterval(() => { void poll(); }, 5000);
        cleanups.push(() => clearInterval(intervalId));
    }

    // Codex review — flip the active-call flag so page-level loggers
    // (audio_user_activity_proxy, audio_memory_pressure) know to emit.
    // Reset call-boundary state on the page logger ONLY for a fresh
    // call. Same-call restarts (re-INVITE / ICE restart) keep the
    // 30 s user-activity debounce and memory threshold-crossed state
    // continuous across the restart — otherwise we'd see phantom
    // first-activity events mid-call and re-fire threshold-crossed
    // when nothing actually crossed.
    setAudioCallActive(true);
    if (isFreshCall) resetAudioPageLoggerCallScopedState();

    logger.info('Audio call logger installed', {
        source: 'sip', event: 'audio_call_logger_installed',
        callId: params.callId,
    });

    const stop = (reason: CallLoggerStopReason = 'call_end') => {
        stopped = true;
        cleanups.forEach(fn => {
            try { fn(); } catch { /* best effort */ }
        });
        if (active?.stop === stop) active = null;
        if (reason === 'call_end') {
            // Real teardown — drop the active-call gate and clear
            // persisted state so a later call (even one reusing
            // callId='unknown') starts from a clean baseline.
            setAudioCallActive(false);
            if (persistedState?.callId === params.callId) persistedState = null;
        }
        // On 'restart' we leave callActive=true and persistedState in
        // place so the next start() resumes the same window.
        logger.info('Audio call logger stopped', {
            source: 'sip', event: 'audio_call_logger_stopped',
            callId: params.callId, reason,
        });
    };
    active = {callId: params.callId, stop};
    return () => stop('call_end');
}

export function stopAudioCallLogger(callId: string): void {
    if (!active) return;
    if (active.callId !== callId) {
        logger.info('Audio call logger: stop for non-active callId, ignoring', {
            source: 'sip', event: 'audio_call_logger_stale_stop',
            requestedCallId: callId, activeCallId: active.callId,
        });
        return;
    }
    active.stop('call_end');
}

// audio_play_call_result — wraps audioEl.play() so every attempt logs a
// resolution outcome with attempt id, trigger, and settle time. The caller
// passes a `trigger` string explaining why the attempt was made; this is
// the most useful field when chasing autoplay rejections.
let playAttemptSeq = 0;
export async function loggedPlay(
    audioElement: HTMLAudioElement,
    callId: string,
    trigger: string,
): Promise<{ok: boolean; errorName?: string; errorMessage?: string; settleMs: number}> {
    const attemptId = ++playAttemptSeq;
    const pausedBefore = audioElement.paused;
    const startPerf = performance.now();
    const common = {
        source: 'sip', event: 'audio_play_call_result',
        callId, attemptId, trigger,
        pausedBefore,
        visibilityState: document.visibilityState,
        documentHasFocus: document.hasFocus(),
        userActivationActive: (navigator as Navigator & {userActivation?: {isActive: boolean}}).userActivation?.isActive ?? null,
    };
    try {
        await audioElement.play();
        const settleMs = performance.now() - startPerf;
        logger.info('Audio play() resolved', {
            ...common, result: 'resolved',
            settleMs, pausedAfter: audioElement.paused,
        });
        return {ok: true, settleMs};
    } catch (err) {
        const settleMs = performance.now() - startPerf;
        const error = err as DOMException;
        bumpDiagCounter('playRejected');
        logger.warn('Audio play() rejected', {
            ...common, result: 'rejected',
            settleMs, pausedAfter: audioElement.paused,
            errorName: error?.name,
            errorMessage: error?.message,
            errorCode: typeof (error as DOMException & {code?: number})?.code === 'number'
                ? (error as DOMException & {code?: number}).code
                : null,
        });
        // audio_autoplay_policy_snapshot — explains "why did autoplay
        // fail right now" by capturing the user-activation state at the
        // exact moment of rejection. NotAllowedError typically means
        // "the user hasn't gestured yet"; AbortError typically means
        // "the element was paused/torn down before play could resolve".
        const userActivation = (navigator as Navigator & {
            userActivation?: {isActive: boolean; hasBeenActive: boolean};
        }).userActivation;
        const mediaSession = (navigator as Navigator & {
            mediaSession?: {playbackState?: string};
        }).mediaSession;
        logger.warn('Autoplay policy snapshot', {
            source: 'sip', event: 'audio_autoplay_policy_snapshot',
            callId, attemptId, trigger,
            failureName: error?.name ?? null,
            failureMessage: error?.message ?? null,
            userActivationIsActive: userActivation?.isActive ?? null,
            userActivationHasBeenActive: userActivation?.hasBeenActive ?? null,
            visibilityState: document.visibilityState,
            hasFocus: document.hasFocus(),
            muted: audioElement.muted,
            volume: audioElement.volume,
            mediaSessionPlaybackState: mediaSession?.playbackState ?? null,
        });
        return {ok: false, errorName: error?.name, errorMessage: error?.message, settleMs};
    }
}
